import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
  Alert,
  InputGroup,
  InputGroupText,
  Badge
} from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import Select from 'react-select';
import { getUserAccessType, getWorkspaceCredits } from '../../../store/Admin';
import CreditHistory from './CreditHistory';

const CreditManagement = ({ dispatch }) => {
  const [creditModal, setCreditModal] = useState(false);
  const [selectedWorkspace, setSelectedWorkspace] = useState(null);
  const [creditForm, setCreditForm] = useState({
    amount: '',
    reason: '',
    workspaceId: ''
  });
  const [showActivitySidebar, setShowActivitySidebar] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const reduxDispatch = useDispatch();

  // Redux state
  const { workspaceCredits, loading, workspaces, userAccessType } = useSelector(state => ({
    workspaceCredits: state.Admin.workspaceCredits || { workspaces: [], stats: {} },
    loading: state.Admin.loading,
    workspaces: state.Admin.workspaces || [],
    userAccessType: state.Admin.userAccessType || { accessType: 'admin', workspaceId: null }
  }));

  useEffect(() => {
    // Fetch user access type first
    reduxDispatch(getUserAccessType());
    console.log('🔄 CreditManagement mounted, fetching data...');
    fetchWorkspaceCredits();
    fetchWorkspaces();
  }, []);

  const fetchWorkspaceCredits = () => {
    console.log('🔄 Dispatching getWorkspaceCredits...');
    reduxDispatch(getWorkspaceCredits());
  };

  const fetchWorkspaces = () => {
    dispatch({
      type: 'GET_ALL_WORKSPACES'
    });
  };

  const handleAddCredits = async () => {
    // Validate form
    if (!creditForm.amount || creditForm.amount <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    if (!creditForm.workspaceId) {
      toast.error('Please select a workspace');
      return;
    }
    if (!creditForm.reason || creditForm.reason.trim() === '') {
      toast.error('Please provide a reason for adding credits');
      return;
    }

    dispatch({
      type: 'ADD_CREDITS',
      payload: creditForm
    });

    // Reset form and close modal
    setCreditModal(false);
    setCreditForm({
      amount: '',
      reason: '',
      workspaceId: ''
    });
    setSelectedWorkspace(null);

    // Refresh data
    setTimeout(() => {
      fetchWorkspaceCredits();
    }, 500);
  };

  const handleWorkspaceSelect = (selected) => {
    if (selected) {
      setSelectedWorkspace(selected);
      setCreditForm({
        ...creditForm,
        workspaceId: selected.value
      });
    }
  };

  const formatCurrency = (amount) => {
    return `$${parseFloat(amount).toFixed(2)}`;
  };

  const formatCredits = (amount) => {
    return parseFloat(amount).toFixed(2);
  };

  const getPriorityBadge = (priority, priorityColor) => {
    const icons = {
      critical: '🔴',
      low: '🟠',
      medium: '🟡',
      high: '🟢'
    };

    return (
      <Badge color={priorityColor} className="px-3 py-1">
        {icons[priority]} {priority.charAt(0).toUpperCase() + priority.slice(1)}
      </Badge>
    );
  };

  const formatPlanName = (planName) => {
    if (!planName || planName === 'N/A') return 'N/A';

    // Parse technical plan names like "Professional_business_month"
    const parts = planName.split('_');
    if (parts.length > 0) {
      let displayName = parts[0].charAt(0).toUpperCase() + parts[0].slice(1);

      // Add billing interval if present
      if (parts.length >= 3) {
        const interval = parts[2];
        if (interval === 'month') displayName += ' (Monthly)';
        else if (interval === 'year') displayName += ' (Yearly)';
      }

      return displayName;
    }

    return planName;
  };

  // Prepare workspace options for select
  const workspaceOptions = workspaces.map(ws => ({
    value: ws._id,
    label: `${ws.name} (ID: ${ws.id}) ${ws.hasSubscription ? '' : '- No subscription'}`,
    workspace: ws
  }));

  const { workspaces: workspacesList = [], stats = {} } = workspaceCredits;

  // Filter workspaces based on search
  const filteredWorkspaces = workspacesList.filter(ws => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      ws.workspaceId?.toLowerCase().includes(search) ||
      ws.workspaceName?.toLowerCase().includes(search) ||
      ws.ownerEmail?.toLowerCase().includes(search)
    );
  });

  // Determine if user is agency
  const isAgencyUser = userAccessType.accessType === 'agency';

  return (
    <div className="">
      {/* Header Section */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h4 className="mb-0">Workspace Credits Overview</h4>
          <small className="text-muted">
            Managing {stats.totalWorkspaces || 0} workspaces with total credits of {formatCurrency(stats.totalCredits || 0)}
          </small>
        </div>
        <div className="d-flex" style={{ gap: '10px' }}>
          <button
            className='btn-style-new'
            onClick={() => setShowActivitySidebar(true)}
          >
            <i className="fa fa-history mr-2"></i>
            Admin Credit History
          </button>
          {!isAgencyUser && (
            <button className='btn-style-new' onClick={() => setCreditModal(true)}>
              + Add Credits
            </button>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      <div className='counter-sections mb-4'>
        <div className='counter-box style-two'>
          <div className='counter-start'>
            <div className='left-text-area'>
              <h5>Critical Priority</h5>
              <div className='CountUp-box'>
                <h3 style={{ color: '#EF4D56' }}>{stats.critical || 0}</h3>
              </div>
              <small className="text-muted">Below 500</small>
            </div>
            <div className='right-icon'>
              <span className='icon'><i className='fa fa-exclamation-triangle' style={{ color: '#EF4D56', fontSize: '18px' }}></i></span>
            </div>
          </div>
        </div>

        <div className='counter-box style-two'>
          <div className='counter-start'>
            <div className='left-text-area'>
              <h5>Low Priority</h5>
              <div className='CountUp-box'>
                <h3 style={{ color: '#FF9500' }}>{stats.low || 0}</h3>
              </div>
              <small className="text-muted">500 - 1,499</small>
            </div>
            <div className='right-icon'>
              <span className='icon'><i className='fa fa-exclamation-circle' style={{ color: '#FF9500', fontSize: '18px' }}></i></span>
            </div>
          </div>
        </div>

        <div className='counter-box style-two'>
          <div className='counter-start'>
            <div className='left-text-area'>
              <h5>Medium Priority</h5>
              <div className='CountUp-box'>
                <h3 style={{ color: '#FFD60A' }}>{stats.medium || 0}</h3>
              </div>
              <small className="text-muted">1,500 - 1,999</small>
            </div>
            <div className='right-icon'>
              <span className='icon'><i className='fa fa-info-circle' style={{ color: '#FFD60A', fontSize: '18px' }}></i></span>
            </div>
          </div>
        </div>

        <div className='counter-box style-two'>
          <div className='counter-start'>
            <div className='left-text-area'>
              <h5>High Priority</h5>
              <div className='CountUp-box'>
                <h3 style={{ color: '#34C759' }}>{stats.high || 0}</h3>
              </div>
              <small className="text-muted">2,000+</small>
            </div>
            <div className='right-icon'>
              <span className='icon'><i className='fa fa-check-circle' style={{ color: '#34C759', fontSize: '18px' }}></i></span>
            </div>
          </div>
        </div>
      </div>

      {/* Workspace Credits Table */}
      <div className="table-container">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h4 className="mb-0">Workspace Credits</h4>
          <div style={{ width: '350px' }}>
            <Input
              type="text"
              placeholder="Search by email, workspace ID or name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="form-control"
            />
          </div>
        </div>
        <div className='table-area'>
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="sr-only">Loading...</span>
              </div>
            </div>
          ) : filteredWorkspaces.length === 0 ? (
            <div className="text-center py-5">
              <i className="fa fa-wallet fa-3x text-muted mb-3"></i>
              <p className="text-muted">
                {searchTerm ? 'No workspaces match your search' : 'No workspaces found'}
              </p>
            </div>
          ) : (
            <div className="table-responsive">
              <Table hover className="mb-0">
                <thead className="bg-light">
                  <tr>
                    <th>Workspace ID</th>
                    <th>Workspace Name</th>
                    <th>Email</th>
                    <th>Plan Name</th>
                    <th>Plan Type</th>
                    <th>Total Agents</th>
                    <th>Credit Balance</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredWorkspaces.map((ws) => (
                    <tr key={ws.workspaceObjectId}>
                      <td>
                        <code style={{ fontSize: '12px', backgroundColor: '#f8f9fa', padding: '4px 8px', borderRadius: '4px' }}>
                          {ws.workspaceId}
                        </code>
                      </td>
                      <td>{ws.workspaceName}</td>
                      <td>
                        <small className="text-muted">{ws.ownerEmail}</small>
                      </td>
                      <td>{formatPlanName(ws.planName)}</td>
                      <td>
                        <Badge color="info" className="px-2 py-1">
                          {ws.planType}
                        </Badge>
                      </td>
                      <td className="text-center">
                        <Badge color="secondary" className="px-2 py-1">
                          {ws.agentCount}
                        </Badge>
                      </td>
                      <td>{formatCredits(ws.creditsBalance)}</td>
                      <td>
                        {getPriorityBadge(ws.priority, ws.priorityColor)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </div>
      </div>

      {/* Add Credits Modal */}
      <Modal isOpen={creditModal} toggle={() => setCreditModal(!creditModal)} size="md">
        <ModalHeader toggle={() => setCreditModal(!creditModal)}>
          Add Credits to Workspace
        </ModalHeader>
        <ModalBody>
          <Form>
            <FormGroup>
              <Label>Select Workspace <span className="text-danger">*</span></Label>
              <Select
                options={workspaceOptions}
                onChange={handleWorkspaceSelect}
                value={workspaceOptions.find(opt => opt.value === creditForm.workspaceId)}
                placeholder="Search and select a workspace..."
                isSearchable
                isClearable
              />
            </FormGroup>

            {selectedWorkspace && (
              <>
                <Alert color="info" className="mt-3">
                  <div className="d-flex justify-content-between mb-2">
                    <span><strong>Workspace:</strong></span>
                    <span>{selectedWorkspace.workspace?.name}</span>
                  </div>
                  <div className="d-flex justify-content-between mb-2">
                    <span><strong>Workspace ID:</strong></span>
                    <span>{selectedWorkspace.workspace?.id}</span>
                  </div>
                  <div className="d-flex justify-content-between mb-2">
                    <span><strong>Subscription Status:</strong></span>
                    <span>
                      {selectedWorkspace.workspace?.hasSubscription ? (
                        <Badge color="success">Active</Badge>
                      ) : (
                        <Badge color="warning">No Subscription (will be created)</Badge>
                      )}
                    </span>
                  </div>
                  <div className="d-flex justify-content-between">
                    <span><strong>Current Balance:</strong></span>
                    <span className="text-primary">
                      <strong>{formatCurrency(selectedWorkspace.workspace?.creditsBalance || 0)}</strong>
                    </span>
                  </div>
                </Alert>
                {!selectedWorkspace.workspace?.hasSubscription && (
                  <Alert color="warning" className="mt-2">
                    <i className="fa fa-info-circle mr-2"></i>
                    A subscription will be automatically created for this workspace when you add credits.
                  </Alert>
                )}
              </>
            )}

            <FormGroup>
              <Label>Add Credit Count  <span className="text-danger">*</span></Label>
              <InputGroup>
                <InputGroupText></InputGroupText>
                <Input
                  type="number"
                  placeholder="Enter amount to add"
                  value={creditForm.amount}
                  onChange={(e) => setCreditForm({ ...creditForm, amount: e.target.value })}
                  min="0.01"
                  step="0.01"
                />
              </InputGroup>
              <small className="text-muted">Enter the dollar amount to add to the workspace's credit balance</small>
            </FormGroup>

            <FormGroup>
              <Label>Reason for Adding Credits <span className="text-danger">*</span></Label>
              <Input
                type="textarea"
                rows="3"
                placeholder="Enter a clear reason for adding these credits..."
                value={creditForm.reason}
                onChange={(e) => setCreditForm({ ...creditForm, reason: e.target.value })}
                maxLength={200}
              />
              <small className="text-muted">
                {creditForm.reason.length}/200 characters
              </small>
            </FormGroup>

            {selectedWorkspace && creditForm.amount > 0 && (
              <Alert color="success" className="mt-3">
                <strong>New Balance After Addition:</strong> {' '}
                {formatCurrency(
                  (selectedWorkspace.workspace?.creditsBalance || 0) +
                  parseFloat(creditForm.amount || 0)
                )}
              </Alert>
            )}
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            onClick={() => {
              setCreditModal(false);
              setCreditForm({ amount: '', reason: '', workspaceId: '' });
              setSelectedWorkspace(null);
            }}
          >
            Cancel
          </Button>
          <Button
            color="primary"
            onClick={handleAddCredits}
            disabled={!creditForm.workspaceId || !creditForm.amount || creditForm.amount <= 0 || !creditForm.reason.trim()}
          >
            <i className="fa fa-plus mr-2"></i>
            Add Credits
          </Button>
        </ModalFooter>
      </Modal>

      {/* Admin Activity Sidebar */}
      <CreditHistory
        isOpen={showActivitySidebar}
        onClose={() => setShowActivitySidebar(false)}
      />
    </div>
  );
};

export default CreditManagement;
